import {
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration,
  MeetingSessionStatus,
  MeetingSessionStatusCode,
  VideoPriorityBasedPolicy,
} from "amazon-chime-sdk-js";
import type { AudioVideoObserver, VideoTileState } from "amazon-chime-sdk-js";
import type { DeviceChangeObserver } from "amazon-chime-sdk-js";
import { ProtectedVideoInputFactory } from "./protectedVideoProcessor";
import type { ProtectedVideoInput } from "./protectedVideoProcessor";
import { runVideoOperation } from "./videoStartupOperation";
import type {
  ChimeAttendeeInfo,
  ChimeMeetingInfo,
  ParticipantRole,
} from "../types/video";

export type ConnectionState =
  | "idle"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "ended"
  | "error";

export type RemoteParticipantState =
  | "waiting"
  | "present-no-video"
  | "video-on"
  | "video-paused"
  | "left";

export interface MeetingEndInfo {
  reason: "ended" | "left" | "network_failure" | "timeout" | "unknown";
  statusCode: MeetingSessionStatusCode;
  message: string;
}

export interface ChimeVideoHandlers {
  onConnectionStateChange?: (state: ConnectionState) => void;
  onRemoteParticipantStateChange?: (state: RemoteParticipantState) => void;
  onLocalVideoStateChange?: (isActive: boolean) => void;
  onLocalVideoDesiredChange?: (desired: boolean) => void;
  onCameraStateChange?: (state: CameraState) => void;
  onAudioActivationRequired?: (required: boolean) => void;
  onVideoActivationRequired?: (required: boolean) => void;
  onRecordingStateChange?: (isRecording: boolean) => void;
  onWarning?: (message: string | null) => void;
  onMeetingEnded?: (endInfo: MeetingEndInfo) => void;
}

export interface CameraState {
  status: "off" | "loading" | "on" | "error";
  mode: "replacement" | "none";
  error: string | null;
}

interface InitializeOptions {
  participantRole: ParticipantRole;
  expiresAt: string;
  recordingStarted: boolean;
}

interface RemoteVideoTile {
  tileId: number;
  attendeeId: string;
  active: boolean;
  paused: boolean;
  sequence: number;
}

type WarningSource = "camera" | "microphone" | "network";
type PlaybackKind = "audio" | "remote" | "local";

interface PlaybackAttempt {
  element: HTMLMediaElement;
  source: number | null;
  result: Promise<boolean>;
  cancel: () => void;
}

const START_TIMEOUT_MS = 20000;
const MEDIA_ACTIVATION_TIMEOUT_MS = 3000;
const RECORDING_STATE_TOPIC = "klchb-recording-state";
const RECORDING_STATE_LIFETIME_MS = 5000;

export class ChimeVideoService {
  private meetingSession: DefaultMeetingSession | null = null;
  private deviceController: DefaultDeviceController | null = null;
  private observer: AudioVideoObserver | null = null;
  private deviceChangeObserver: DeviceChangeObserver | null = null;
  private handlers: ChimeVideoHandlers = {};
  private localVideoElement: HTMLVideoElement | null = null;
  private remoteVideoElement: HTMLVideoElement | null = null;
  private audioElement: HTMLAudioElement | null = null;
  private localTileId: number | null = null;
  private remoteTileId: number | null = null;
  private remoteAttendeeId: string | null = null;
  private remoteTilesByAttendee = new Map<string, RemoteVideoTile>();
  private presentRemoteAttendeeIds = new Set<string>();
  private hasRemoteParticipantJoined = false;
  private presenceHandler:
    | ((attendeeId: string, present: boolean, externalUserId?: string) => void)
    | null = null;
  private visibilityHandler: (() => void) | null = null;
  private recordingState = false;
  private participantRole: ParticipantRole | null = null;
  private meetingId: string | null = null;
  private isStopping = false;
  private isAudioVideoStarted = false;
  private isAudioInputStarted = false;
  private isLocalVideoStarted = false;
  private isLocalVideoDesired = false;
  private startResolve: (() => void) | null = null;
  private startReject: ((error: Error) => void) | null = null;
  private startOperationReject: ((error: Error) => void) | null = null;
  private audioVideoStartPromise: Promise<void> | null = null;
  private startTimer: ReturnType<typeof setTimeout> | null = null;
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;
  private stopPromise: Promise<void> | null = null;
  private audioInputOperation: Promise<void> | null = null;
  private videoInputOperation: Promise<void> | null = null;
  private audioInputStream: MediaStream | undefined;
  private videoInputStream: MediaStream | undefined;
  private cameraStream: MediaStream | undefined;
  private cameraAbort: AbortController | null = null;
  private backgroundInput: ProtectedVideoInput | null = null;
  private backgroundFactory = new ProtectedVideoInputFactory();
  private backgroundCleanup: Promise<void> = Promise.resolve();
  private pendingVideoBind: Promise<MediaStream | undefined> | null = null;
  private pendingCameraCapture: Promise<MediaStream> | null = null;
  private cameraState: CameraState = { status: "off", mode: "replacement", error: null };
  private cameraDeviceIndex = 0;
  private cameraNeedsReset = false;
  private videoIntentRevision = 0;
  private isAudioMuted = false;
  private playbackAttempts = new Map<PlaybackKind, PlaybackAttempt>();
  private videoActivationRequired = { local: false, remote: false };
  private sessionGeneration = 0;
  private remoteTileSequence = 0;
  private warnings = new Map<WarningSource, string>();

  setHandlers(handlers: ChimeVideoHandlers): void {
    this.handlers = handlers;
  }

  async initialize(
    meetingInfo: ChimeMeetingInfo,
    attendeeInfo: ChimeAttendeeInfo,
    options: InitializeOptions,
  ): Promise<void> {
    const expiryDelay = this.getExpiryDelay(options.expiresAt);
    if (this.stopPromise) {
      await this.stopPromise;
    }
    if (this.meetingId && this.meetingId !== meetingInfo.meetingId) {
      await this.stop();
    }
    if (this.meetingSession && this.meetingId === meetingInfo.meetingId) {
      return;
    }

    if (!navigator.mediaDevices) {
      throw new Error("瀏覽器無法存取麥克風與攝影機");
    }

    this.participantRole = options.participantRole;
    this.cameraState = { status: "off", mode: "replacement", error: null };
    this.cameraDeviceIndex = 0;
    this.handlers.onCameraStateChange?.(this.cameraState);
    this.recordingState = options.recordingStarted;
    this.handlers.onRecordingStateChange?.(this.recordingState);
    this.meetingId = meetingInfo.meetingId;
    const generation = ++this.sessionGeneration;
    const logger = new ConsoleLogger("ChimeSDK", LogLevel.WARN);
    this.deviceController = new DefaultDeviceController(logger);
    this.deviceChangeObserver = {
      audioInputStreamEnded: () => {
        if (!this.isCurrentSession(generation)) return;
        this.isAudioInputStarted = false;
        this.setWarning(
          "microphone",
          "麥克風串流已中斷；回到本頁時會嘗試恢復。",
        );
      },
      videoInputStreamEnded: () => {
        if (!this.isCurrentSession(generation)) return;
        this.clearLocalPlayback();
        this.isLocalVideoStarted = false;
        this.localTileId = null;
        this.cameraNeedsReset = true;
        this.handlers.onLocalVideoStateChange?.(false);
        if (this.isLocalVideoDesired) {
          this.setWarning(
            "camera",
            "攝影機串流已中斷；回到本頁時會嘗試恢復，也可手動重試攝影機。",
          );
        }
      },
    };
    this.deviceController.addDeviceChangeObserver(this.deviceChangeObserver);

    const configuration = new MeetingSessionConfiguration(
      {
        MeetingId: meetingInfo.meetingId,
        MediaRegion: meetingInfo.mediaRegion,
        MediaPlacement: meetingInfo.mediaPlacement,
      },
      {
        AttendeeId: attendeeInfo.attendeeId,
        ExternalUserId: attendeeInfo.externalUserId,
        JoinToken: attendeeInfo.joinToken,
      },
    );
    // A one-to-one consultation does not benefit from multi-layer encoding.
    configuration.enableSVC = false;
    configuration.enableSimulcastForUnifiedPlanChromiumBasedBrowsers = false;
    configuration.keepLastFrameWhenPaused = true;
    configuration.videoDownlinkBandwidthPolicy = new VideoPriorityBasedPolicy(
      logger,
    );

    this.meetingSession = new DefaultMeetingSession(
      configuration,
      logger,
      this.deviceController,
    );
    this.registerObservers(generation);
    this.scheduleExpiry(expiryDelay, generation);
  }

  async startAudioVideo(): Promise<void> {
    if (!this.meetingSession || !this.deviceController) {
      throw new Error("視訊尚未初始化");
    }
    if (this.isAudioVideoStarted) {
      return;
    }
    if (this.audioVideoStartPromise) {
      return this.audioVideoStartPromise;
    }

    const startPromise = this.doStartAudioVideo();
    this.audioVideoStartPromise = startPromise;
    try {
      await startPromise;
    } finally {
      if (this.audioVideoStartPromise === startPromise) {
        this.audioVideoStartPromise = null;
      }
    }
  }

  private async doStartAudioVideo(): Promise<void> {
    const generation = this.sessionGeneration;
    let rejectStopped!: (error: Error) => void;
    const stopped = new Promise<never>((_, reject) => {
      rejectStopped = reject;
    });
    this.startOperationReject = rejectStopped;

    try {
      this.handlers.onConnectionStateChange?.("connecting");
      await Promise.race([this.prepareAudioInput(), stopped]);
      if (this.audioElement) {
        void this.bindAudioElement(this.audioElement);
      }
      if (!this.isCurrentSession(generation) || !this.meetingSession) {
        throw new Error("視訊連線已停止");
      }

      const started = new Promise<void>((resolve, reject) => {
        this.startResolve = resolve;
        this.startReject = reject;
        this.startTimer = setTimeout(() => {
          this.startReject?.(new Error("視訊連線逾時，請檢查網路後重試"));
          this.clearStartWaiter();
        }, START_TIMEOUT_MS);
      });
      this.meetingSession.audioVideo.start();
      await Promise.race([started, stopped]);
    } catch (error) {
      if (this.isCurrentSession(generation)) {
        await this.stop();
      }
      throw error;
    } finally {
      if (this.startOperationReject === rejectStopped) {
        this.startOperationReject = null;
      }
    }
  }

  async startLocalVideo(): Promise<void> {
    if (!this.meetingSession || !this.deviceController) {
      throw new Error("視訊尚未初始化");
    }
    if (!this.isAudioVideoStarted || this.isStopping) {
      throw new Error("通話尚未連線，無法開啟攝影機");
    }
    if (!this.isLocalVideoDesired) this.videoIntentRevision += 1;
    this.isLocalVideoDesired = true;
    this.handlers.onLocalVideoDesiredChange?.(true);
    if (this.cameraState.status === "error") this.setCameraState("off");
    await this.updateVideoInput();
  }

  async retryProtectedVideo(): Promise<void> {
    this.changeCameraIntent("replacement");
    await this.startLocalVideo();
  }

  /** Only call for an explicit user action to turn background replacement off. */
  async enableRawVideo(): Promise<void> {
    this.changeCameraIntent("none");
    await this.startLocalVideo();
  }

  async switchCamera(): Promise<void> {
    if (!this.isLocalVideoDesired) return;
    this.cameraDeviceIndex += 1;
    this.changeCameraIntent(this.cameraState.mode);
    await this.startLocalVideo();
  }

  private changeCameraIntent(mode: CameraState["mode"]): void {
    this.videoIntentRevision += 1;
    this.cameraNeedsReset = true;
    this.cameraAbort?.abort();
    this.closeCameraOutput();
    this.cameraState = { status: "off", mode, error: null };
    this.handlers.onCameraStateChange?.(this.cameraState);
  }

  async resumeAudioPlayback(): Promise<boolean> {
    if (!this.audioElement || !this.meetingSession || this.isStopping) {
      return false;
    }
    const playback = this.bindAudioElement(this.audioElement, true);
    if (
      !this.isAudioInputStarted ||
      this.streamNeedsRecovery(this.audioInputStream)
    ) {
      void this.prepareAudioInput();
    }
    return playback;
  }

  async resumeVideoPlayback(): Promise<boolean> {
    if (!this.meetingSession || this.isStopping) {
      return false;
    }
    const attempts: Promise<boolean>[] = [];
    // Both play calls run in the gesture stack; neither waits for the other.
    if (this.remoteVideoElement && this.remoteTileId !== null) {
      attempts.push(
        this.bindVideoElement(this.remoteTileId, this.remoteVideoElement, true),
      );
    }
    if (
      this.videoActivationRequired.local &&
      this.isLocalVideoDesired &&
      this.localVideoElement &&
      this.localTileId !== null
    ) {
      attempts.push(
        this.bindVideoElement(this.localTileId, this.localVideoElement, true),
      );
    }
    return attempts.length > 0 && (await Promise.all(attempts)).every(Boolean);
  }

  broadcastRecordingState(isRecording: boolean): void {
    if (!this.meetingSession || this.participantRole !== "consultant") {
      return;
    }
    this.recordingState = isRecording;
    this.handlers.onRecordingStateChange?.(isRecording);
    this.meetingSession.audioVideo.realtimeSendDataMessage(
      RECORDING_STATE_TOPIC,
      JSON.stringify({ isRecording }),
      RECORDING_STATE_LIFETIME_MS,
    );
  }

  setLocalVideoElement(element: HTMLVideoElement | null): void {
    if (element !== this.localVideoElement)
      this.playbackAttempts.get("local")?.cancel();
    this.localVideoElement = element;
    if (element && this.localTileId !== null) {
      this.bindVideoElement(this.localTileId, element);
    }
  }

  setRemoteVideoElement(element: HTMLVideoElement | null): void {
    if (element !== this.remoteVideoElement)
      this.playbackAttempts.get("remote")?.cancel();
    this.remoteVideoElement = element;
    if (element && this.remoteTileId !== null) {
      this.bindVideoElement(this.remoteTileId, element);
    }
  }

  setAudioElement(element: HTMLAudioElement | null): void {
    if (element !== this.audioElement)
      this.playbackAttempts.get("audio")?.cancel();
    this.audioElement = element;
    if (element && this.meetingSession) {
      void this.bindAudioElement(element);
    } else if (!element) {
      this.meetingSession?.audioVideo.unbindAudioElement();
    }
  }

  muteAudio(): void {
    this.isAudioMuted = true;
    this.meetingSession?.audioVideo.realtimeMuteLocalAudio();
  }

  unmuteAudio(): void {
    this.isAudioMuted = false;
    this.meetingSession?.audioVideo.realtimeUnmuteLocalAudio();
  }

  async stopLocalVideo(): Promise<void> {
    if (!this.meetingSession || !this.deviceController) {
      return;
    }
    this.isLocalVideoDesired = false;
    this.clearLocalPlayback();
    this.videoIntentRevision += 1;
    this.cameraAbort?.abort();
    this.handlers.onLocalVideoDesiredChange?.(false);
    this.closeCameraOutput();
    this.setCameraState("off");
    this.setWarning("camera", null);
    // Neither a model nor an SDK permission/processing queue can hold this action.
    void this.updateVideoInput().catch(() => undefined);
  }

  async stop(): Promise<void> {
    if (this.stopPromise) {
      return this.stopPromise;
    }
    this.stopPromise = this.doStop();
    try {
      await this.stopPromise;
    } finally {
      this.stopPromise = null;
    }
  }

  private async doStop(): Promise<void> {
    this.isStopping = true;
    this.sessionGeneration += 1;
    this.videoIntentRevision += 1;
    this.isLocalVideoDesired = false;
    this.cameraAbort?.abort();
    this.closeCameraOutput();
    this.playbackAttempts.forEach((attempt) => attempt.cancel());
    this.stopStream(this.videoInputStream);
    this.stopStream(this.audioInputStream);
    const stoppedError = new Error("視訊連線已停止");
    this.startOperationReject?.(stoppedError);
    this.startOperationReject = null;
    this.startReject?.(stoppedError);
    this.clearStartWaiter();
    if (this.expiryTimer) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }

    try {
      const audioVideo = this.meetingSession?.audioVideo;
      if (audioVideo) {
        const presenceHandler = this.presenceHandler;
        const observer = this.observer;
        if (presenceHandler) {
          this.safelyRun(() =>
            audioVideo.realtimeUnsubscribeToAttendeeIdPresence(presenceHandler),
          );
        }
        if (observer) {
          this.safelyRun(() => audioVideo.removeObserver(observer));
        }
        this.safelyRun(() =>
          audioVideo.realtimeUnsubscribeFromReceiveDataMessage(
            RECORDING_STATE_TOPIC,
          ),
        );
        this.safelyRun(() => audioVideo.unbindAudioElement());
        this.safelyRun(() => audioVideo.stop());
      }
      if (this.visibilityHandler) {
        document.removeEventListener(
          "visibilitychange",
          this.visibilityHandler,
        );
      }
      if (this.deviceChangeObserver) {
        this.safelyRun(() =>
          this.deviceController?.removeDeviceChangeObserver(
            this.deviceChangeObserver!,
          ),
        );
      }
      // SDK device queues can still be waiting for a browser permission prompt.
      // Let this session end; late streams are released by the captured operation.
      if (this.deviceController) {
        await this.waitForDeviceCleanup(this.deviceController);
      }
    } finally {
      this.meetingSession = null;
      this.deviceController = null;
      this.observer = null;
      this.deviceChangeObserver = null;
      this.presenceHandler = null;
      this.visibilityHandler = null;
      this.meetingId = null;
      this.participantRole = null;
      this.localTileId = null;
      this.remoteTileId = null;
      this.remoteAttendeeId = null;
      this.remoteTilesByAttendee.clear();
      this.presentRemoteAttendeeIds.clear();
      this.hasRemoteParticipantJoined = false;
      this.isAudioVideoStarted = false;
      this.isAudioInputStarted = false;
      this.isLocalVideoStarted = false;
      this.isLocalVideoDesired = false;
      this.recordingState = false;
      this.audioInputOperation = null;
      this.videoInputOperation = null;
      this.audioInputStream = undefined;
      this.videoInputStream = undefined;
      this.cameraStream = undefined;
      this.cameraAbort = null;
      this.cameraState = { status: "off", mode: "replacement", error: null };
      this.cameraNeedsReset = false;
      this.videoActivationRequired = { local: false, remote: false };
      this.isAudioMuted = false;
      this.audioVideoStartPromise = null;
      this.remoteTileSequence = 0;
      this.warnings.clear();
      this.isStopping = false;
    }
  }

  isLocalVideoRunning(): boolean {
    return this.isLocalVideoStarted;
  }

  isLocalVideoRequested(): boolean {
    return this.isLocalVideoDesired;
  }

  private registerObservers(generation: number): void {
    if (!this.meetingSession) {
      return;
    }

    this.observer = {
      audioVideoDidStartConnecting: (reconnecting: boolean) => {
        if (!this.isCurrentSession(generation)) return;
        this.handlers.onConnectionStateChange?.(
          reconnecting ? "reconnecting" : "connecting",
        );
      },
      audioVideoDidStart: () => {
        if (!this.isCurrentSession(generation)) return;
        this.isAudioVideoStarted = true;
        if (this.isAudioMuted)
          this.meetingSession?.audioVideo.realtimeMuteLocalAudio();
        this.handlers.onConnectionStateChange?.("connected");
        this.startResolve?.();
        this.clearStartWaiter();
        if (this.audioElement) {
          void this.bindAudioElement(this.audioElement);
        }
        this.resumeBoundMedia(generation);
      },
      audioVideoDidStop: (status: MeetingSessionStatus) => {
        if (!this.isCurrentSession(generation)) return;
        this.isAudioVideoStarted = false;
        this.startReject?.(new Error("無法建立視訊連線"));
        this.clearStartWaiter();
        if (this.isStopping) {
          return;
        }

        const endInfo = this.toMeetingEndInfo(status);
        this.handlers.onConnectionStateChange?.(
          endInfo.reason === "ended" ? "ended" : "error",
        );
        this.handlers.onMeetingEnded?.(endInfo);
        void this.stop();
      },
      videoTileDidUpdate: (tileState: VideoTileState) => {
        if (!this.isCurrentSession(generation)) return;
        this.handleVideoTileUpdate(tileState);
      },
      videoTileWasRemoved: (tileId: number) => {
        if (!this.isCurrentSession(generation)) return;
        if (tileId === this.localTileId) {
          this.clearLocalPlayback();
          this.localTileId = null;
          this.isLocalVideoStarted = false;
          this.cameraNeedsReset = this.isLocalVideoDesired;
          this.handlers.onLocalVideoStateChange?.(false);
          return;
        }
        this.removeRemoteTile(tileId);
      },
      connectionDidBecomePoor: () => {
        if (!this.isCurrentSession(generation)) return;
        this.setWarning("network", "目前網路不穩，影像可能暫時降低品質。");
      },
      connectionDidBecomeGood: () => {
        if (!this.isCurrentSession(generation)) return;
        this.setWarning("network", null);
      },
      connectionDidSuggestStopVideo: () => {
        if (!this.isCurrentSession(generation)) return;
        this.setWarning(
          "network",
          "目前網路頻寬不足，若畫面持續卡頓可先關閉自己的攝影機。",
        );
      },
    };
    this.meetingSession.audioVideo.addObserver(this.observer);

    this.meetingSession.audioVideo.realtimeSubscribeToReceiveDataMessage(
      RECORDING_STATE_TOPIC,
      (dataMessage) => {
        if (!this.isCurrentSession(generation)) return;
        if (
          this.participantRole !== "user" ||
          !this.isExpectedRemote(dataMessage.senderExternalUserId) ||
          dataMessage.throttled
        ) {
          return;
        }
        try {
          const parsed = JSON.parse(dataMessage.text()) as {
            isRecording?: unknown;
          };
          if (typeof parsed.isRecording === "boolean") {
            this.recordingState = parsed.isRecording;
            this.handlers.onRecordingStateChange?.(parsed.isRecording);
          }
        } catch {
          // Ignore malformed real-time messages without affecting the call.
        }
      },
    );

    this.presenceHandler = (
      attendeeId: string,
      present: boolean,
      externalUserId?: string,
    ) => {
      if (!this.isCurrentSession(generation)) return;
      if (!this.isExpectedRemote(externalUserId)) {
        return;
      }
      if (present) {
        this.hasRemoteParticipantJoined = true;
        this.presentRemoteAttendeeIds.add(attendeeId);
        if (this.participantRole === "consultant" && this.recordingState) {
          this.broadcastRecordingState(true);
        }
        if (this.remoteTileId === null) {
          this.handlers.onRemoteParticipantStateChange?.("present-no-video");
        }
      } else {
        this.presentRemoteAttendeeIds.delete(attendeeId);
        this.removeRemoteAttendee(attendeeId);
      }
    };
    this.meetingSession.audioVideo.realtimeSubscribeToAttendeeIdPresence(
      this.presenceHandler,
    );

    this.visibilityHandler = () => {
      if (
        document.visibilityState !== "visible" ||
        !this.isCurrentSession(generation)
      ) {
        return;
      }
      this.resumeBoundMedia(generation);
    };
    document.addEventListener("visibilitychange", this.visibilityHandler);
  }

  private handleVideoTileUpdate(tileState: VideoTileState): void {
    if (tileState.tileId === null || tileState.isContent) {
      return;
    }
    if (tileState.localTile) {
      if (!this.isLocalVideoDesired || this.cameraState.status !== "on") return;
      this.localTileId = tileState.tileId;
      this.isLocalVideoStarted = Boolean(
        tileState.localTileStarted || tileState.active,
      );
      if (this.localVideoElement) {
        this.bindVideoElement(tileState.tileId, this.localVideoElement);
      }
      this.handlers.onLocalVideoStateChange?.(this.isLocalVideoStarted);
      return;
    }
    if (!this.isExpectedRemote(tileState.boundExternalUserId ?? undefined)) {
      return;
    }

    if (!tileState.boundAttendeeId) return;
    this.hasRemoteParticipantJoined = true;
    this.presentRemoteAttendeeIds.add(tileState.boundAttendeeId);
    this.remoteTilesByAttendee.set(tileState.boundAttendeeId, {
      tileId: tileState.tileId,
      attendeeId: tileState.boundAttendeeId,
      active: Boolean(tileState.active),
      paused: Boolean(tileState.paused || tileState.poorConnection),
      sequence: ++this.remoteTileSequence,
    });
    this.selectRemoteTile();
  }

  private bindVideoElement(
    tileId: number,
    element: HTMLVideoElement,
    manual = false,
  ): Promise<boolean> {
    const audioVideo = this.meetingSession?.audioVideo;
    if (!audioVideo || this.isStopping) return Promise.resolve(false);
    const kind = tileId === this.remoteTileId ? "remote" : "local";
    try {
      // A tile can keep its id while the SDK replaces its stream.
      audioVideo.bindVideoElement(tileId, element);
    } catch (error) {
      return this.trackPlayback(
        kind,
        element,
        tileId,
        () => Promise.reject(error),
        true,
      );
    }
    return this.trackPlayback(
      kind,
      element,
      tileId,
      () => element.play(),
      manual,
    );
  }

  private resumeBoundMedia(generation: number): void {
    if (!this.isCurrentSession(generation)) return;
    // No shared await/lock: a pending microphone or autoplay cannot hold video.
    if (
      !this.isAudioInputStarted ||
      this.streamNeedsRecovery(this.audioInputStream)
    ) {
      void this.prepareAudioInput();
    }
    if (!this.isCurrentSession(generation)) return;
    this.synchronizeVideoTiles();
    if (this.localVideoElement && this.localTileId !== null) {
      this.bindVideoElement(this.localTileId, this.localVideoElement);
    }
    if (this.remoteVideoElement && this.remoteTileId !== null) {
      this.bindVideoElement(this.remoteTileId, this.remoteVideoElement);
    }
    if (this.audioElement) {
      void this.bindAudioElement(this.audioElement);
    }
    if (!this.isCurrentSession(generation)) return;
    if (
      this.isLocalVideoDesired &&
      this.cameraState.status !== "error" &&
      (!this.isLocalVideoStarted ||
        this.streamNeedsRecovery(this.videoInputStream) ||
        this.streamNeedsRecovery(this.cameraStream))
    ) {
      void this.updateVideoInput().catch(() => undefined);
    }
  }

  private synchronizeVideoTiles(): void {
    const tiles = this.meetingSession?.audioVideo.getAllVideoTiles() ?? [];
    this.remoteTilesByAttendee.clear();
    let hasLocalTile = false;
    for (const tile of tiles) {
      const state = tile.state();
      if (state.localTile) hasLocalTile = true;
      this.handleVideoTileUpdate(state);
    }
    if (!hasLocalTile && this.localTileId !== null) {
      this.clearLocalPlayback();
      this.localTileId = null;
      this.isLocalVideoStarted = false;
      this.cameraNeedsReset = this.isLocalVideoDesired;
      this.handlers.onLocalVideoStateChange?.(false);
    }
    this.selectRemoteTile();
  }

  private bindAudioElement(
    element: HTMLAudioElement,
    manual = false,
  ): Promise<boolean> {
    const audioVideo = this.meetingSession?.audioVideo;
    if (!audioVideo || this.isStopping) return Promise.resolve(false);
    return this.trackPlayback(
      "audio",
      element,
      null,
      () => {
        // Invoke all three in the click stack, before awaiting anything.
        const resume = this.invokeMedia(() =>
          DefaultDeviceController.resumeAudioContext(),
        );
        const bind = this.invokeMedia(() =>
          audioVideo.bindAudioElement(element),
        );
        const play = this.invokeMedia(() => element.play());
        return Promise.all([resume, bind, play]);
      },
      manual,
    );
  }

  private prepareAudioInput(): Promise<void> {
    const controller = this.deviceController;
    if (!controller || this.isStopping) return Promise.resolve();
    if (this.audioInputOperation) return this.audioInputOperation;
    const operation = this.doPrepareAudioInput(
      controller,
      this.sessionGeneration,
    );
    this.audioInputOperation = operation;
    void operation.then(() => {
      if (this.audioInputOperation === operation)
        this.audioInputOperation = null;
    });
    return operation;
  }

  private async doPrepareAudioInput(
    controller: DefaultDeviceController,
    generation: number,
  ): Promise<void> {
    try {
      if (
        this.audioInputStream &&
        (!this.isAudioInputStarted ||
          this.streamNeedsRecovery(this.audioInputStream))
      ) {
        // Re-selecting the same device may reuse a live-but-muted SDK stream.
        this.stopStream(this.audioInputStream);
        this.audioInputStream = undefined;
        this.isAudioInputStarted = false;
        await controller.stopAudioInput();
        if (!this.isCurrentSession(generation)) return;
      }
      const devices = await controller.listAudioInputDevices(true);
      if (!this.isCurrentSession(generation)) return;
      const stream = await controller.startAudioInput(
        devices.length > 0 ? devices[0].deviceId : null,
      );
      if (!this.isCurrentSession(generation)) {
        this.stopStream(stream);
        return;
      }
      this.audioInputStream = stream;
      this.isAudioInputStarted =
        devices.length > 0 && !this.streamNeedsRecovery(stream);
      if (this.isAudioMuted)
        this.meetingSession?.audioVideo.realtimeMuteLocalAudio();
      if (devices.length === 0) {
        this.setWarning("microphone", "找不到麥克風，您仍可聽到對方聲音。");
      } else if (!this.isAudioInputStarted) {
        this.setWarning(
          "microphone",
          "麥克風串流仍未恢復，請檢查裝置權限後再開啟聲音。",
        );
      } else {
        this.setWarning("microphone", null);
      }
    } catch {
      if (!this.isCurrentSession(generation)) return;
      this.isAudioInputStarted = false;
      try {
        const stream = await controller.startAudioInput(null);
        if (!this.isCurrentSession(generation)) {
          this.stopStream(stream);
          return;
        }
        this.audioInputStream = stream;
      } catch {
        // The meeting can still receive remote audio without a local input.
      }
      if (!this.isCurrentSession(generation)) return;
      this.setWarning(
        "microphone",
        "麥克風無法開啟，您仍可接收聲音。請檢查瀏覽器權限。",
      );
    }
  }

  private updateVideoInput(): Promise<void> {
    const controller = this.deviceController;
    const session = this.meetingSession;
    if (!controller || !session || this.isStopping) return Promise.resolve();
    if (this.videoInputOperation) return this.videoInputOperation;
    const operation = this.reconcileVideoInput(
      controller,
      session,
      this.sessionGeneration,
    );
    this.videoInputOperation = operation;
    const clear = () => {
      if (this.videoInputOperation === operation)
        this.videoInputOperation = null;
    };
    void operation.then(clear, clear);
    return operation;
  }

  private async reconcileVideoInput(
    controller: DefaultDeviceController,
    session: DefaultMeetingSession,
    generation: number,
  ): Promise<void> {
    while (this.isCurrentSession(generation)) {
      const revision = this.videoIntentRevision;
      const abort = new AbortController();
      this.cameraAbort = abort;
      const deadline = Date.now() + 30_000;
      const step = <T,>(start: () => Promise<T>, disposeLate?: (value: T) => void | Promise<void>) =>
        runVideoOperation({ start, signal: abort.signal, timeoutMs: Math.max(1, Math.min(15_000, deadline - Date.now())), disposeLate });
      try {
        if (
          !this.isLocalVideoDesired ||
          this.cameraNeedsReset ||
          this.streamNeedsRecovery(this.videoInputStream) ||
          this.streamNeedsRecovery(this.cameraStream)
        ) {
          this.closeCameraOutput();
          await step(() => controller.stopVideoInput());
          if (!this.isCurrentSession(generation)) return;
          this.cameraNeedsReset = false;
          if (revision !== this.videoIntentRevision) continue;
          if (!this.isLocalVideoDesired) return;
        }
        if (this.isLocalVideoStarted) return;
        this.setCameraState("loading");
        // A late SDK bind still owns its controller queue. Never stack another model.
        if (this.pendingVideoBind) throw new Error("攝影機仍在釋放資源，請稍後重試去背。");
        if (this.pendingCameraCapture) throw new Error("攝影機權限仍在等待回應，請先完成或取消權限視窗。");
        await step(() => this.backgroundCleanup);
        const devices = await step(() => controller.listVideoInputDevices(true));
        if (!this.isCurrentSession(generation)) return;
        if (revision !== this.videoIntentRevision || !this.isLocalVideoDesired)
          continue;
        if (devices.length === 0) throw new Error("找不到可用的攝影機");
        controller.chooseVideoInputQuality(640, 360, 15);
        const deviceId = devices[this.cameraDeviceIndex % devices.length].deviceId;
        const capture = navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { deviceId: { exact: deviceId }, width: { ideal: 640 }, height: { ideal: 360 }, frameRate: { ideal: 15, max: 15 } },
        });
        this.pendingCameraCapture = capture;
        const clearCapture = () => { if (this.pendingCameraCapture === capture) this.pendingCameraCapture = null; };
        void capture.then(clearCapture, clearCapture);
        const cameraStream = await step(() => capture, (late) => this.stopStream(late));
        if (!this.isCurrentSession(generation) || revision !== this.videoIntentRevision || abort.signal.aborted) {
          this.stopStream(cameraStream);
          continue;
        }
        this.cameraStream = cameraStream;
        let input: MediaStream | ProtectedVideoInput["device"] = cameraStream;
        if (this.cameraState.mode === "replacement") {
          const protectedInput = await step(() => this.backgroundFactory.create(
            new ConsoleLogger("ChimeBackground", LogLevel.WARN), cameraStream, abort.signal,
            () => {
              if (this.isCurrentSession(generation) && revision === this.videoIntentRevision) {
                this.failCamera("背景影像處理已停止，攝影機關閉；仍可使用語音。");
                abort.abort();
              }
            },
          ), (late) => late.dispose());
          if (!this.isCurrentSession(generation) || revision !== this.videoIntentRevision || abort.signal.aborted) {
            void protectedInput.dispose().catch(() => undefined);
            continue;
          }
          this.backgroundInput = protectedInput;
          input = protectedInput.device;
        }
        const binding = controller.startVideoInput(input);
        this.pendingVideoBind = binding;
        const clearBinding = () => { if (this.pendingVideoBind === binding) this.pendingVideoBind = null; };
        void binding.then(clearBinding, clearBinding);
        const protectedDevice = this.backgroundInput?.device;
        const stream = await step(() => binding, async (late) => {
          this.stopStream(late);
          // SDK bind may finish allocating its pipeline after the first disposal.
          if (protectedDevice) await protectedDevice.stop();
        });
        if (!this.isCurrentSession(generation)) {
          this.stopStream(stream);
          return;
        }
        if (
          revision !== this.videoIntentRevision ||
          !this.isLocalVideoDesired
        ) {
          this.stopStream(stream);
          this.cameraNeedsReset = true;
          continue;
        }
        this.videoInputStream = stream;
        if (this.backgroundInput) await step(() => this.backgroundInput!.ready);
        if (abort.signal.aborted || !this.isCurrentSession(generation) || revision !== this.videoIntentRevision) continue;
        this.setCameraState("on");
        this.isLocalVideoStarted = true;
        session.audioVideo.startLocalVideoTile();
        this.setWarning("camera", null);
        return;
      } catch (error) {
        if (!this.isCurrentSession(generation)) return;
        if (revision !== this.videoIntentRevision) continue;
        abort.abort();
        if (this.cameraState.status !== "error") this.failCamera(
          error instanceof Error && error.name !== "NotAllowedError" && error.message !== "NotAllowedError" ? error.message
            : "攝影機無法開啟，您仍可使用語音進行諮詢。請檢查瀏覽器權限。",
        );
        throw error;
      }
    }
  }

  private setCameraState(status: CameraState["status"], error: string | null = null): void {
    this.cameraState = { ...this.cameraState, status, error };
    this.handlers.onCameraStateChange?.(this.cameraState);
  }

  private failCamera(message: string): void {
    this.closeCameraOutput();
    this.cameraNeedsReset = true;
    this.setCameraState("error", message);
    this.setWarning("camera", message);
  }

  private closeCameraOutput(): void {
    this.clearLocalPlayback();
    this.safelyRun(() => this.meetingSession?.audioVideo.stopLocalVideoTile());
    this.stopStream(this.cameraStream);
    this.stopStream(this.videoInputStream);
    this.cameraStream = undefined;
    this.videoInputStream = undefined;
    this.isLocalVideoStarted = false;
    this.localTileId = null;
    if (this.localVideoElement) this.localVideoElement.srcObject = null;
    this.handlers.onLocalVideoStateChange?.(false);
    const background = this.backgroundInput;
    this.backgroundInput = null;
    if (background) {
      this.backgroundCleanup = background.dispose();
      void this.backgroundCleanup.catch(() => undefined);
    }
  }

  private streamNeedsRecovery(stream: MediaStream | undefined): boolean {
    return stream
      ? stream
          .getTracks()
          .some((track) => track.readyState === "ended" || track.muted)
      : false;
  }

  private stopStream(stream: MediaStream | undefined): void {
    stream?.getTracks().forEach((track) => this.safelyRun(() => track.stop()));
  }

  private invokeMedia(action: () => Promise<unknown>): Promise<unknown> {
    try {
      return Promise.resolve(action());
    } catch (error) {
      return Promise.reject(error);
    }
  }

  private trackPlayback(
    kind: PlaybackKind,
    element: HTMLMediaElement,
    source: number | null,
    action: () => Promise<unknown>,
    manual: boolean,
  ): Promise<boolean> {
    if (!this.meetingSession || this.isStopping) return Promise.resolve(false);
    const existing = this.playbackAttempts.get(kind);
    if (!manual && existing?.element === element && existing.source === source)
      return existing.result;
    existing?.cancel();
    const generation = this.sessionGeneration;
    let resolve!: (success: boolean) => void;
    const result = new Promise<boolean>((done) => {
      resolve = done;
    });
    let settled = false;
    const finish = (success: boolean, notify = true) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (this.playbackAttempts.get(kind) === attempt) {
        this.playbackAttempts.delete(kind);
        if (notify && this.isCurrentSession(generation)) {
          if (kind === "audio" && this.audioElement === element)
            this.handlers.onAudioActivationRequired?.(!success);
          if (
            kind === "remote" &&
            this.remoteVideoElement === element &&
            this.remoteTileId === source
          ) {
            this.setVideoActivationRequired("remote", !success);
          }
          if (
            kind === "local" &&
            this.isLocalVideoDesired &&
            this.localVideoElement === element &&
            this.localTileId === source
          ) {
            this.setVideoActivationRequired("local", !success);
          }
        }
      }
      resolve(success);
    };
    const timer = setTimeout(() => finish(false), MEDIA_ACTIVATION_TIMEOUT_MS);
    const attempt: PlaybackAttempt = {
      element,
      source,
      result,
      cancel: () => finish(false, false),
    };
    this.playbackAttempts.set(kind, attempt);
    void this.invokeMedia(action).then(
      () => finish(true),
      () => finish(false),
    );
    return result;
  }

  private waitForDeviceCleanup(
    controller: DefaultDeviceController,
  ): Promise<void> {
    return new Promise((resolve) => {
      const timer = setTimeout(resolve, MEDIA_ACTIVATION_TIMEOUT_MS);
      void this.invokeMedia(() => controller.destroy()).then(
        () => {
          clearTimeout(timer);
          resolve();
        },
        () => {
          clearTimeout(timer);
          resolve();
        },
      );
    });
  }

  private setVideoActivationRequired(
    kind: "local" | "remote",
    required: boolean,
  ): void {
    this.videoActivationRequired[kind] = required;
    this.handlers.onVideoActivationRequired?.(
      this.videoActivationRequired.local || this.videoActivationRequired.remote,
    );
  }

  private clearLocalPlayback(): void {
    this.playbackAttempts.get("local")?.cancel();
    this.setVideoActivationRequired("local", false);
  }

  private isExpectedRemote(externalUserId?: string): boolean {
    if (!externalUserId || !this.participantRole) {
      return false;
    }
    const expectedPrefix =
      this.participantRole === "consultant" ? "user:" : "consultant:";
    return externalUserId.startsWith(expectedPrefix);
  }

  private scheduleExpiry(delay: number, generation: number): void {
    this.expiryTimer = setTimeout(
      () => {
        if (!this.isCurrentSession(generation)) return;
        const endInfo: MeetingEndInfo = {
          reason: "timeout",
          statusCode: MeetingSessionStatusCode.MeetingEnded,
          message: "視訊會議已達 30 分鐘上限",
        };
        this.handlers.onConnectionStateChange?.("ended");
        this.handlers.onMeetingEnded?.(endInfo);
        void this.stop();
      },
      Math.min(delay, 2_147_483_647),
    );
  }

  private getExpiryDelay(expiresAt: string): number {
    const delay = new Date(expiresAt).getTime() - Date.now();
    if (!Number.isFinite(delay) || delay <= 0) {
      throw new Error("視訊會議已過期");
    }
    return delay;
  }

  private removeRemoteTile(tileId: number): void {
    let attendeeToRemove: string | null = null;
    this.remoteTilesByAttendee.forEach((tile, attendeeId) => {
      if (tile.tileId === tileId) {
        attendeeToRemove = attendeeId;
      }
    });
    if (attendeeToRemove) {
      this.remoteTilesByAttendee.delete(attendeeToRemove);
    }
    this.selectRemoteTile();
  }

  private removeRemoteAttendee(attendeeId: string): void {
    this.remoteTilesByAttendee.delete(attendeeId);
    this.selectRemoteTile();
  }

  private selectRemoteTile(): void {
    const tiles = Array.from(this.remoteTilesByAttendee.values()).sort(
      (left, right) => right.sequence - left.sequence,
    );
    const current = this.remoteAttendeeId
      ? tiles.find(
          (tile) =>
            tile.attendeeId === this.remoteAttendeeId &&
            tile.active &&
            !tile.paused,
        )
      : null;
    const selected =
      current ??
      tiles.find((tile) => tile.active && !tile.paused) ??
      tiles[0] ??
      null;

    this.remoteTileId = selected?.tileId ?? null;
    this.remoteAttendeeId = selected?.attendeeId ?? null;
    // Binding may emit another tile update; publish this snapshot first so it
    // cannot overwrite the newer active state from that callback.
    this.handlers.onRemoteParticipantStateChange?.(
      selected?.active && !selected.paused
        ? "video-on"
        : selected?.paused
          ? "video-paused"
          : this.presentRemoteAttendeeIds.size > 0
            ? "present-no-video"
            : this.hasRemoteParticipantJoined
              ? "left"
              : "waiting",
    );
    if (!selected) {
      this.playbackAttempts.get("remote")?.cancel();
      this.setVideoActivationRequired("remote", false);
    } else if (this.remoteVideoElement && !selected.paused) {
      // Chime's active flag requires a bound video element. Waiting for active
      // before binding leaves both peers permanently without remote video.
      this.bindVideoElement(selected.tileId, this.remoteVideoElement);
    }
  }

  private setWarning(source: WarningSource, message: string | null): void {
    if (message) {
      this.warnings.set(source, message);
    } else {
      this.warnings.delete(source);
    }
    const priority: WarningSource[] = ["camera", "microphone", "network"];
    const current = priority
      .map((warningSource) => this.warnings.get(warningSource))
      .find(Boolean);
    this.handlers.onWarning?.(current ?? null);
  }

  private isCurrentSession(generation: number): boolean {
    return !this.isStopping && generation === this.sessionGeneration;
  }

  private toMeetingEndInfo(status: MeetingSessionStatus): MeetingEndInfo {
    const statusCode = status.statusCode();
    if (statusCode === MeetingSessionStatusCode.MeetingEnded) {
      return {
        reason: "ended",
        statusCode,
        message: "諮詢團隊已結束會議",
      };
    }
    if (statusCode === MeetingSessionStatusCode.Left) {
      return { reason: "left", statusCode, message: "您已離開會議" };
    }
    if (statusCode === MeetingSessionStatusCode.AudioJoinedFromAnotherDevice) {
      return {
        reason: "unknown",
        statusCode,
        message: "此視訊已在其他裝置開啟",
      };
    }
    return {
      reason: status.isFailure() ? "network_failure" : "unknown",
      statusCode,
      message: status.isFailure() ? "網路連線中斷" : "視訊已停止",
    };
  }

  private safelyRun(action: () => void): void {
    try {
      action();
    } catch {
      // Continue releasing the remaining Chime resources.
    }
  }

  private clearStartWaiter(): void {
    if (this.startTimer) {
      clearTimeout(this.startTimer);
      this.startTimer = null;
    }
    this.startResolve = null;
    this.startReject = null;
  }
}
